<template>
  <v-container>
    <template v-if="subscriptionID === null">
      <v-layout row wrap>
        <v-flex xs12>
          <v-list
            two-line
          >
            <v-list-tile v-if="subscriptions.length === 0" to="/subscribe">
              <v-list-tile-content>
                <v-list-tile-title>
                  No subscriptions. See here for information on how to subscribe.
                </v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
            <v-list-group
              v-else
              v-for="subscription in subscriptions"
              :key="subscription.id"
            >
              <template v-slot:activator>
                <v-list-tile>
                  <v-list-tile-avatar>
                    <v-icon :color="subscription.iconColor">{{ subscription.icon }}</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title>
                      {{ subscription.title}} ({{ subscription.terminalDate ? 'ending ' + subscription.terminalDate.toLocaleDateString() : 'no end date' }})
                    </v-list-tile-title>
                    <v-list-tile-sub-title>
                      {{ subscription.nextCycle ? 'Next billing cycle on ' + subscription.nextCycle.toLocaleDateString() : 'No further billing cycles' }}
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
              </template>

              <v-list-tile
                v-for="item in subscription.actions"
                :key="item.title"
                @click="item.action(subscription, item)"
                :disabled="item.isLoading || item.isUnavailable"
              >
                <v-list-tile-content>
                  <v-list-tile-title>
                    {{ item.title }}
                  </v-list-tile-title>
                  <v-list-tile-sub-title>
                    <v-progress-linear
                      v-if="item.isLoading"
                      indeterminate
                      height="3"
                    />
                    <span v-else>{{ item.subtitle || '' }}</span>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list-group>
          </v-list>
        </v-flex>
      </v-layout>
    </template>
    <template v-else>
      <v-layout row wrap justify-center>
        <template v-if="!subscription">
          <v-flex xs12 text-xs-center>
            <v-progress-circular
              size="75"
              width="5"
              color="accent"
              class="ma-5"
              indeterminate
            />
          </v-flex>
        </template>
        <template v-else>
          <v-flex xs12 text-xs-center>
            <v-icon
              size="75"
              :color="subscription.iconColor"
            >
              {{ subscription.icon }}
            </v-icon>
          </v-flex>
          <v-flex xs12 text-xs-center>
            <h1>{{ subscription.title }}</h1>
          </v-flex>
          <v-flex xs12>
            <v-progress-linear
              color="accent"
              indeterminate
              v-if="detailsLoading"
            />
            <v-timeline
              dense
            >
              <v-timeline-item
                v-for="item in timelineItems"
                :key="item.id"
                :icon="item.icon"
                small
              >
                <v-layout row wrap justify-space-between>
                  <v-flex xs9 sm7>
                    {{ item.description }}
                  </v-flex>
                  <v-flex shrink>
                    {{ item.when.toLocaleDateString() }}
                  </v-flex>
                  <v-flex shrink>
                    {{ item.when.toLocaleTimeString() }}
                  </v-flex>
                  <v-flex xs12>
                    <span class="caption font-italic">{{ item.details }}</span>
                  </v-flex>
                  <v-flex v-if="item.actions">
                    <v-btn
                      v-for="action in item.actions"
                      :key="action.title"
                      @click="action.action"
                    >
                      {{ action.title }}
                    </v-btn>
                  </v-flex>
                </v-layout>
              </v-timeline-item>
            </v-timeline>
          </v-flex>
        </template>
      </v-layout>
    </template>
  </v-container>
</template>

<script>
export default {
  data: () => ({
    subscriptions: [],
    timelineItems: [],

    // Detail holders
    detailsLoading: false,
    subscriptionPaypalPayments: [],
    subscriptionInvoices: [],

    billingMethods: []
  }),
  computed: {
    subscriptionID: function () {
      if (typeof this.$route.params.id !== 'undefined') {
        return parseInt(this.$route.params.id)
      } else {
        return null
      }
    },
    subscription: function () {
      if (this.subscriptionID !== null && this.subscriptions.length > 0) {
        return this.subscriptions.find((sub) => {
          return sub.id === this.subscriptionID
        })
      } else {
        return null
      }
    }
  },
  watch: {
    // We watch the subscription object so we can populate its timeline
    subscription: async function () {
      // If we couldn't find our subscription, go back to the listing page
      if (this.subscription === undefined) {
        return this.$router.push('/subscriptions')
      }

      // If there's no active subscription, we're done
      if (this.subscription === null) {
        return
      }

      // Start off with the current date as a baseline item
      this.timelineItems = [
        { id: 0, index: 0, when: new Date(), description: 'You are here', icon: 'star' }
      ]

      // Add our basic facts from the subscription data
      let props = [
        [ 'created_at', 'Subscription initiated' ],
        [ 'approvalDate', 'Payment is approved' ],
        [ 'startDate', 'Subscription becomes active' ],
        [ 'endDate', 'Billing cycle ends' ],
        [ 'cancelDate', 'Subscription is canceled' ],
        [ 'terminalDate', 'Subscription expires' ]
      ]
      props.forEach((prop, i) => {
        let k = prop[0]
        if (this.subscription[k] !== null) {
          this.timelineItems.push({
            id: Math.random(),
            index: i,
            when: this.subscription[k],
            description: prop[1]
          })
        }
      })

      // Make sure our items are in the right order
      this.sortTimeline()

      // If our subscription is backed by PayPal, load in payment history
      if (this.subscription.ppSubscriptionID !== null) {
        this.detailsLoading = true
        // Make sure we have the data on record
        if (typeof this.subscriptionPaypalPayments[this.subscription.id] === 'undefined') {
          let res = await (
            this.axios.get('/subscriptions/billing/paypal/payments/' + this.subscription.ppSubscriptionID)
              .catch(() => {}) // this information is optional, so we just swallow failure
          )

          if (typeof res !== 'undefined') {
            // Parse our date values into something more usable
            for (let p of res.data) {
              p.time = this.$root.parseApiDateTimeString(p.time)
            }

            // Store our payment history
            this.subscriptionPaypalPayments[this.subscription.id] = res.data
          } else {
            this.subscriptionPaypalPayments[this.subscription.id] = []
          }
        }

        // Add our payment data to the timeline and re-sort
        for (let p of this.subscriptionPaypalPayments[this.subscription.id]) {
          this.timelineItems.push({
            id: p.id,
            when: p.time,
            description: 'PayPal payment: $' + p.amount_with_breakdown.gross_amount.value,
            details: 'Transaction ' + p.id,
            icon: 'payment'
          })
        }
        this.sortTimeline()
      }

      // Handle any invoices that exist for this subscription
      let res = await (
        this.axios.get('/invoices/for/subscription/' + this.subscription.id)
          .catch(() => {}) // this information is optional, so we just swallow failure
      )
      if (typeof res !== 'undefined') {
        // Parse our date values into something more usable
        for (let invoice of res.data) {
          let fields = [
            'created_at',
            'dateDue',
            'datePaid',
            'periodStart',
            'periodEnd'
          ]
          for (let field of fields) {
            if (invoice[field]) {
              invoice[field] = this.$root.parseApiDateTimeString(invoice[field])
            }
          }
        }

        this.subscriptionInvoices[this.subscription.id] = res.data
      } else {
        this.subscriptionInvoices[this.subscription.id] = []
      }
      if (this.subscriptionInvoices[this.subscription.id].length > 0) {
        // If we had any invoices, add their dates to the timeline and re-sort
        for (let invoice of this.subscriptionInvoices[this.subscription.id]) {
          let details = 'Due on ' + invoice.dateDue.toLocaleDateString() + ' at ' + invoice.dateDue.toLocaleTimeString()
          if (invoice.datePaid !== null) {
            details = 'Paid in full on ' + invoice.datePaid.toLocaleDateString()
          }
          this.timelineItems.push({
            id: 'invoice-' + invoice.id,
            when: invoice.created_at,
            description: 'Invoice #' + invoice.id + ' created',
            details,
            icon: 'receipt',
            actions: [
              { title: 'View Details', action: () => { this.$router.push('/invoices/' + invoice.id) } }
            ]
          })
        }
        this.sortTimeline()
      }

      // Mark our loading as complete
      this.detailsLoading = false
    }
  },
  methods: {
    sortTimeline: function () {
      this.timelineItems.sort((a, b) => {
        // Try to sort by time first
        if (a.when > b.when) {
          return -1
        }
        if (b.when > a.when) {
          return 1
        }

        // If time is equal, try to sort by index (to indicate logical if not
        // actual sequence)
        if (a.index || b.index) {
          if (a.index > b.index) {
            return -1
          }
          if (b.index > a.index) {
            return 1
          }
        }

        return 0
      })
    },
    approveSubscription: function (subscription, item) {
      if (subscription.ppApprovalURL !== null) {
        item.isLoading = true
        window.location.href = subscription.ppApprovalURL
      }
    },
    extendSubscription: function (subscription, item) {
      item.isLoading = true
      this.$router.push('/subscribe')
    },
    cancelSubscription: function (subscription, item) {
      // Make sure we want to do this before sending the request
      if (!confirm('If you cancel this subscription, it will not create any new payment requirements and will remain active until ' + subscription.endDate.toLocaleDateString() + '. Are you sure you want to cancel this subscription?')) {
        return
      }

      item.isLoading = true
      this.axios.delete('/subscriptions/' + subscription.id)
        .then(() => {
          // Set some provisional date values to update our display to match
          // what we expect the API will say the next time the data is refreshed
          let now = new Date()
          subscription.cancelDate = now
          subscription.terminalDate = subscription.endDate

          // Remove the option to cancel
          item.isUnavailable = true
          item.title += ' (canceled on ' + now.toLocaleDateString() + ')'
        })
        .catch(this.$root.apiErrorHandler())
        .finally(() => {
          item.isLoading = false
        })
    }
  },
  mounted: async function () {
    // Get our list of subscriptions
    this.subscriptions = (
      await (this.$auth.subscriptions().catch(this.$root.apiErrorHandler()))
    ).map((sub) => {
      // Initialize some required display values
      sub.title = 'Loading...'
      sub.icon = 'hourglass_empty'
      sub.iconColor = 'blue'

      // Set our action items for this subscription
      sub.actions = []
      let title
      let isUnavailable
      // - View History
      sub.actions.push({
        title: 'View History',
        action: () => { this.$router.push('/subscriptions/' + sub.id) }
      })
      // - Cancel
      title = 'Cancel Subscription'
      isUnavailable = false
      if (sub.cancelDate !== null) {
        title += ' (canceled on ' + sub.cancelDate.toLocaleDateString() + ')'
        isUnavailable = true
      } else if (sub.nextCycle === null) {
        isUnavailable = true
      }
      sub.actions.push({
        title,
        action: this.cancelSubscription,
        isLoading: false,
        isUnavailable
      })

      return sub
    })

    // Now that we have basic subscription data populated (and rendered), we can
    // go back through and fill in some details
    for (let i of this.subscriptions.keys()) {
      let sub = this.subscriptions[i]
      // If we don't already have information about this subscription's billing
      // method, then populate that data
      if (typeof this.billingMethods[sub.billingMethodID] === 'undefined') {
        let res = await (
          this.axios.get('/subscriptions/billing/methods/' + sub.billingMethodID)
            .catch(this.$root.apiErrorHandler())
        )
        this.billingMethods[sub.billingMethodID] = res.data[0]
      }

      // Set a title for the subscription
      sub.title = this.billingMethods[sub.billingMethodID].title + ' Subscription'

      // Update our icon based on the subscription's status
      let now = new Date()
      // - If the subscription has a terminal date in the past, it's expired;
      // or if it has a start date in the future, it's pending
      if ((sub.terminalDate !== null && sub.terminalDate < now) ||
          (sub.startDate > now)) {
        sub.icon = 'history'
        sub.iconColor = 'grey'
      }

      // - If the subscription currently granting a subscription, it is active
      if (sub.isActive) {
        sub.icon = 'check_circle_outline'
        sub.iconColor = 'green'
      }

      // - If the subscription hasn't been approved, the user needs to take action
      // unless the subscription has also been canceled
      if (sub.approvalDate === null && sub.cancelDate === null) {
        sub.icon = 'error_outline'
        sub.iconColor = 'red'

        // Add an "approve" action for this subscription
        sub.actions.unshift({
          title: 'Approve Payment',
          subtitle: 'You must finish the payment process to activate this subscription',
          isLoading: false,
          action: this.approveSubscription
        })
      }

      // - If the subscription ended before it "started" then it was canceled
      // during the free cancelation period
      if (sub.terminalDate !== null && sub.terminalDate < sub.startDate) {
        sub.icon = 'cancel'
        sub.iconColor = 'grey'
      }

      // - If the most recent current subscription is nearing its terminal date, we
      // need to start notifying
      if (i === 0 && sub.startDate <= now && sub.nextCycle === null) {
        sub.icon = 'warning'
        sub.iconColor = 'yellow'

        // Add an "extend" action for this subscription
        sub.actions.unshift({
          title: 'Extend Subscription',
          subtitle: 'This is the final billing cycle of your most recent subscription',
          isLoading: false,
          action: this.extendSubscription
        })

        // Mark that we should show a subscription warning to the user
        this.$root.isSubscriptionExpiring = true
      }

      // - If the subscription has a hold, there's a problem
      if (sub.hasActiveHold) {
        sub.icon = 'error_outline'
        sub.iconColor = 'red'
      }
    }
  }
}
</script>
